import { motion } from "framer-motion";
import {
  EnvelopeIcon,
  PhoneIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { FaLinkedinIn } from "react-icons/fa";
import duckflowLogo from "assets/img/logo.png";

// ── Data ──────────────────────────────────────────────────────────────────────

const PERSON = {
  name: "Ibrahim Akgül",
  title: "CTO & Co-fondateur",
  company: "DuckFlow",
  tagline:
    "Je construis la couche d'intelligence du marché CHR français — en connectant données avancées, IA et équipes commerciales.",
  bio: "Je co-fonde et dirige DuckFlow sur le plan technique et produit. Au quotidien, mon travail consiste à construire un système capable de détecter, qualifier et contextualiser les évolutions du marché CHR en temps réel — pour que nos clients puissent identifier les bonnes opportunités commerciales, au bon moment, avant leurs concurrents.",
  bio2: "Ce qui nous différencie : une spécialisation sectorielle forte sur le CHR, combinée à une capacité à capter des signaux que la plupart des plateformes généralistes ne traitent pas, enrichis et structurés grâce à l'IA.",
  contact: {
    email: "ibrahim@duckflow.io",
    phone: "+33 7 56 95 86 21",
    linkedin: "https://www.linkedin.com/in/ibrahim-akgüll-457995178/",
    website: "https://www.duckflow.io/",
  },
};

const DUCKFLOW = {
  tagline: "Plateforme de prospection CHR intelligente",
  description:
    "DuckFlow aide les entreprises qui vendent au secteur CHR (cafés, hôtels, restaurants) à repérer les bons établissements au bon moment. La plateforme identifie automatiquement les évolutions du marché, enrichit les informations pertinentes et transforme ces signaux en opportunités commerciales claires, qualifiées et actionnables.",
  stats: [
    { value: "+15K", label: "Établissements suivis" },
    { value: "243", label: "Signaux / semaine" },
    { value: "91%", label: "Taux de qualification" },
    { value: "24/7", label: "Monitoring automatique" },
  ],
  features: [
    "Détection des ouvertures de restaurants",
    "Changements de propriétaire",
    "Multi-propriétaires & expansion",
    "Signaux de franchise",
    "Scoring IA automatique",
    "Alertes temps réel",
    "Intégration CRM native",
    "Export de contacts qualifiés",
    "Couverture nationale CHR",
    "Base de données restaurants",
  ],
  value: [
    {
      icon: "🎯",
      title: "Voir le marché avant tout le monde",
      desc: "Identifiez les opportunités avant qu'elles ne deviennent visibles partout.",
    },
    {
      icon: "🤖",
      title: "Qualification par IA",
      desc: "Les signaux sont automatiquement scorés, enrichis et priorisés pour la pertinence commerciale.",
    },
    {
      icon: "⚡",
      title: "Agir au bon moment",
      desc: "Alertes en temps réel envoyées directement dans votre CRM au moment opportun.",
    },
    {
      icon: "🏆",
      title: "Données à profondeur sectorielle",
      desc: "La base de données CHR la plus complète de France — conçue pour les équipes commerciales.",
    },
  ],
};

// ── Animation helper ──────────────────────────────────────────────────────────

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5, ease: "easeOut" },
} as const;


// ── Top bar ───────────────────────────────────────────────────────────────────

const TopBar = () => (
  <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-sm border-b border-gray-200">
    <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
      <a
        href={PERSON.contact.website}
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-2 text-gray-900 hover:opacity-70 transition-opacity"
      >
        <img src={duckflowLogo} alt="DuckFlow" className="w-7 h-7 object-contain" />
        <span className="text-sm font-semibold tracking-tight">DuckFlow</span>
      </a>
      <a
        href={`mailto:${PERSON.contact.email}`}
        className="inline-flex items-center gap-1.5 text-xs font-medium bg-black text-white px-3 py-1.5 rounded hover:bg-gray-800 transition-colors"
      >
        <EnvelopeIcon className="w-3.5 h-3.5" />
        Me contacter
      </a>
    </div>
  </header>
);

// ── Hero ──────────────────────────────────────────────────────────────────────

const Hero = () => (
  <section className="bg-white border-b border-gray-200">
    <div className="max-w-4xl mx-auto px-6 py-14 sm:py-20">
      <motion.div {...fadeUp} className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-8">
        <div className="w-16 h-16 rounded-xl bg-black flex items-center justify-center flex-shrink-0">
          <span className="text-white text-lg font-semibold tracking-wide">IA</span>
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">
            {PERSON.name}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {PERSON.title} —{" "}
            <a
              href={PERSON.contact.website}
              target="_blank"
              rel="noreferrer"
              className="text-gray-900 font-medium hover:underline"
            >
              {PERSON.company}
            </a>
          </p>
        </div>
      </motion.div>

      <motion.p
        {...fadeUp}
        transition={{ ...fadeUp.transition, delay: 0.05 }}
        className="text-sm text-gray-600 max-w-2xl leading-relaxed mb-8"
      >
        {PERSON.tagline}
      </motion.p>

      {/* Contact links */}
      <motion.div
        {...fadeUp}
        transition={{ ...fadeUp.transition, delay: 0.1 }}
        className="flex flex-wrap items-center gap-3"
      >
        <a
          href={`mailto:${PERSON.contact.email}`}
          className="inline-flex items-center gap-2 text-xs text-gray-700 border border-gray-200 px-3 py-2 rounded hover:border-gray-400 hover:bg-gray-50 transition-colors"
        >
          <EnvelopeIcon className="w-3.5 h-3.5 text-gray-500" />
          {PERSON.contact.email}
        </a>
        <a
          href={`tel:${PERSON.contact.phone.replace(/\s/g, "")}`}
          className="inline-flex items-center gap-2 text-xs text-gray-700 border border-gray-200 px-3 py-2 rounded hover:border-gray-400 hover:bg-gray-50 transition-colors"
        >
          <PhoneIcon className="w-3.5 h-3.5 text-gray-500" />
          {PERSON.contact.phone}
        </a>
        <a
          href={PERSON.contact.linkedin}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 text-xs text-gray-700 border border-gray-200 px-3 py-2 rounded hover:border-gray-400 hover:bg-gray-50 transition-colors"
        >
          <FaLinkedinIn className="w-3.5 h-3.5 text-[#0A66C2]" />
          LinkedIn
        </a>
        <a
          href={PERSON.contact.website}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 text-xs text-gray-700 border border-gray-200 px-3 py-2 rounded hover:border-gray-400 hover:bg-gray-50 transition-colors"
        >
          <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 text-gray-500" />
          duckflow.io
        </a>
      </motion.div>
    </div>
  </section>
);

// ── About ─────────────────────────────────────────────────────────────────────

const About = () => (
  <section className="bg-gray-50 border-b border-gray-200">
    <div className="max-w-4xl mx-auto px-6 py-12">
      <motion.div {...fadeUp} className="max-w-2xl space-y-4">
        <p className="text-sm text-gray-600 leading-relaxed">{PERSON.bio}</p>
        <p className="text-sm text-gray-600 leading-relaxed">{PERSON.bio2}</p>
      </motion.div>
    </div>
  </section>
);

// ── DuckFlow Product ──────────────────────────────────────────────────────────

const ProductSection = () => (
  <section className="bg-white border-b border-gray-200">
    <div className="max-w-4xl mx-auto px-6 py-12">
      {/* Header */}
      <motion.div {...fadeUp} className="mb-8">
        <p className="text-xs font-medium text-gray-400 uppercase tracking-widest mb-2">
          Le produit
        </p>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">DuckFlow</h2>
        <p className="text-xs text-gray-500 font-medium mb-4">{DUCKFLOW.tagline}</p>
        <p className="text-sm text-gray-600 leading-relaxed max-w-2xl">
          {DUCKFLOW.description}
        </p>
      </motion.div>

      {/* Stats row */}
      <motion.div
        {...fadeUp}
        transition={{ ...fadeUp.transition, delay: 0.05 }}
        className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10"
      >
        {DUCKFLOW.stats.map((s) => (
          <div key={s.label} className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <p className="text-2xl font-semibold text-gray-900">{s.value}</p>
            <p className="text-xs text-gray-500 mt-1 leading-tight">{s.label}</p>
          </div>
        ))}
      </motion.div>

      {/* Value props */}
      <motion.div
        {...fadeUp}
        transition={{ ...fadeUp.transition, delay: 0.1 }}
        className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10"
      >
        {DUCKFLOW.value.map((v) => (
          <div key={v.title} className="flex gap-3 p-4 bg-gray-50 border border-gray-200 rounded-xl">
            <span className="text-lg flex-shrink-0 mt-0.5">{v.icon}</span>
            <div>
              <p className="text-sm font-medium text-gray-900">{v.title}</p>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed">{v.desc}</p>
            </div>
          </div>
        ))}
      </motion.div>

      {/* Features */}
      <motion.div
        {...fadeUp}
        transition={{ ...fadeUp.transition, delay: 0.15 }}
      >
        <p className="text-xs font-medium text-gray-400 uppercase tracking-widest mb-4">
          Fonctionnalités
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6">
          {DUCKFLOW.features.map((f) => (
            <div key={f} className="flex items-center gap-2">
              <CheckCircleIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-700">{f}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);

// ── Contact footer ────────────────────────────────────────────────────────────

const ContactFooter = () => (
  <footer className="bg-gray-50">
    <div className="max-w-4xl mx-auto px-6 py-12">
      <motion.div {...fadeUp} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
        <div>
          <p className="text-xs font-medium text-gray-400 uppercase tracking-widest mb-2">
            Me contacter
          </p>
          <h2 className="text-lg font-semibold text-gray-900 mb-1">
            Ibrahim Akgül
          </h2>
          <p className="text-sm text-gray-500">CTO & Co-fondateur — DuckFlow</p>
        </div>

        <div className="flex flex-col gap-2">
          <a
            href={`mailto:${PERSON.contact.email}`}
            className="inline-flex items-center gap-2 text-sm font-medium bg-black text-white px-4 py-2.5 rounded hover:bg-gray-800 transition-colors"
          >
            <EnvelopeIcon className="w-4 h-4" />
            {PERSON.contact.email}
          </a>
          <div className="flex gap-2">
            <a
              href={`tel:${PERSON.contact.phone.replace(/\s/g, "")}`}
              className="flex-1 inline-flex items-center justify-center gap-2 text-gray-700 border border-gray-200 px-3 py-2 rounded hover:bg-white hover:border-gray-400 transition-colors"
            >
              <PhoneIcon className="w-4 h-4 text-gray-500" />
              <span className="text-xs">{PERSON.contact.phone}</span>
            </a>
            <a
              href={PERSON.contact.linkedin}
              target="_blank"
              rel="noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-2 text-gray-700 border border-gray-200 px-3 py-2 rounded hover:bg-white hover:border-gray-400 transition-colors"
            >
              <FaLinkedinIn className="w-4 h-4 text-[#0A66C2]" />
              <span className="text-xs">LinkedIn</span>
            </a>
          </div>
        </div>
      </motion.div>

      <motion.div
        {...fadeUp}
        transition={{ ...fadeUp.transition, delay: 0.05 }}
        className="mt-10 pt-6 border-t border-gray-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
      >
        <div className="flex items-center gap-2">
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500" />
          </span>
          <span className="text-xs text-gray-500">Disponible pour des échanges</span>
        </div>
        <a
          href={PERSON.contact.website}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-gray-400 hover:text-gray-700 transition-colors inline-flex items-center gap-1"
        >
          duckflow.io
          <ArrowTopRightOnSquareIcon className="w-3 h-3" />
        </a>
      </motion.div>
    </div>
  </footer>
);

// ── Page ──────────────────────────────────────────────────────────────────────

const IbrahimPage = () => (
  <div className="min-h-screen bg-white font-dm">
    <TopBar />
    <Hero />
    <About />
    <ProductSection />
    <ContactFooter />
  </div>
);

export default IbrahimPage;
